import { AlertTriangle, CheckCircle2, Globe2, KeyRound, Server, ShieldCheck } from "lucide-react";

import { Input } from "@/design";
import { cn } from "@/lib/cn";
import type { Account, AccountProxyInput, ProxyType } from "@/api/accounts";

export interface ProxyFormState {
  enabled: boolean;
  proxy_type: ProxyType;
  host: string;
  port: string;
  username: string;
  password: string;
  country_code: string;
}

const TYPES: { value: ProxyType; label: string }[] = [
  { value: "socks5", label: "SOCKS5" },
  { value: "socks4", label: "SOCKS4" },
  { value: "http", label: "HTTP" },
];

export function emptyProxyForm(): ProxyFormState {
  return {
    enabled: false,
    proxy_type: "socks5",
    host: "",
    port: "",
    username: "",
    password: "",
    country_code: "",
  };
}

export function proxyFormFromAccount(account: Account | null): ProxyFormState {
  if (!account?.proxy) return emptyProxyForm();
  return {
    enabled: true,
    proxy_type: account.proxy.proxy_type,
    host: account.proxy.host,
    port: String(account.proxy.port),
    username: account.proxy.username ?? "",
    password: "",
    country_code: account.proxy.country_code === "custom" ? "" : (account.proxy.country_code ?? ""),
  };
}

export function isProxyFormValid(value: ProxyFormState): boolean {
  if (!value.enabled) return true;
  const port = Number(value.port);
  return Boolean(value.host.trim()) && Number.isInteger(port) && port >= 1 && port <= 65535;
}

export function proxyInputFromForm(value: ProxyFormState): AccountProxyInput | null {
  if (!value.enabled) return null;
  return {
    proxy_type: value.proxy_type,
    host: value.host.trim(),
    port: Number(value.port),
    username: value.username.trim() || null,
    password: value.password.trim() || null,
    country_code: value.country_code.trim().toLowerCase() || "custom",
  };
}

export function proxyFormKey(value: ProxyFormState): string {
  return JSON.stringify({
    enabled: value.enabled,
    proxy_type: value.proxy_type,
    host: value.host.trim(),
    port: value.port.trim(),
    username: value.username.trim(),
    country_code: value.country_code.trim().toLowerCase(),
  });
}

interface Props {
  value: ProxyFormState;
  onChange: (value: ProxyFormState) => void;
  disabled?: boolean;
  current?: Account["proxy"];
}

export function ProxySettingsForm({ value, onChange, disabled, current }: Props) {
  const update = (patch: Partial<ProxyFormState>) => onChange({ ...value, ...patch });
  const invalid = value.enabled && !isProxyFormValid(value);

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => update({ enabled: !value.enabled })}
        disabled={disabled}
        className={cn(
          "w-full rounded-2xl border p-4 text-left transition-colors",
          value.enabled
            ? "border-accent/35 bg-accent-soft"
            : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]",
          disabled && "opacity-60 pointer-events-none",
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "size-10 rounded-xl grid place-items-center shrink-0",
              value.enabled ? "bg-accent/20 text-accent" : "bg-white/[0.04] text-text-muted",
            )}
          >
            <ShieldCheck className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold text-[15px]">Прокси для аккаунта</div>
              <span
                className={cn(
                  "h-6 w-10 rounded-full p-0.5 transition-colors",
                  value.enabled ? "bg-accent" : "bg-white/[0.12]",
                )}
              >
                <span
                  className={cn(
                    "block size-5 rounded-full bg-white transition-transform",
                    value.enabled && "translate-x-4",
                  )}
                />
              </span>
            </div>
            <div className="mt-1 text-xs leading-relaxed text-text-muted">
              {value.enabled
                ? "Telegram-сессия будет подключаться только через этот прокси."
                : "Можно оставить прямое подключение или закрепить отдельный прокси."}
            </div>
          </div>
        </div>
      </button>

      {value.enabled && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {TYPES.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => update({ proxy_type: item.value })}
                disabled={disabled}
                className={cn(
                  "h-10 rounded-xl text-xs font-semibold transition-colors",
                  value.proxy_type === item.value
                    ? "bg-accent text-white"
                    : "bg-white/[0.04] text-text-muted hover:bg-white/[0.07]",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-[1fr_104px] gap-2">
            <Input
            label="Хост"
              value={value.host}
              onChange={(e) => update({ host: e.target.value })}
              leftIcon={<Server />}
              disabled={disabled}
              placeholder="1.2.3.4"
              autoCapitalize="off"
              autoCorrect="off"
            />
            <Input
            label="Порт"
              value={value.port}
              onChange={(e) => update({ port: e.target.value.replace(/\D/g, "").slice(0, 5) })}
              disabled={disabled}
              placeholder="1080"
              inputMode="numeric"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Input
              label="Логин"
              value={value.username}
              onChange={(e) => update({ username: e.target.value })}
              leftIcon={<Globe2 />}
              disabled={disabled}
              placeholder="необязательно"
              autoCapitalize="off"
              autoCorrect="off"
            />
            <Input
              label="Пароль"
              type="password"
              value={value.password}
              onChange={(e) => update({ password: e.target.value })}
              leftIcon={<KeyRound />}
              disabled={disabled}
              placeholder={current?.has_password ? "сохранён" : "необязательно"}
              autoCapitalize="off"
              autoCorrect="off"
            />
          </div>

          <Input
            label="Код страны"
            value={value.country_code}
            onChange={(e) => update({ country_code: e.target.value.slice(0, 8).toLowerCase() })}
            placeholder="us"
            disabled={disabled}
            hint="Для пометки в карточке, не влияет на маршрутизацию."
          />

          {invalid && (
            <div className="flex items-center gap-2 rounded-xl border border-danger/20 bg-danger/10 px-3 py-2 text-xs text-danger">
              <AlertTriangle className="size-4 shrink-0" />
              Укажите host и порт от 1 до 65535.
            </div>
          )}

          {current && (
            <div
              className={cn(
                "flex items-start gap-2 rounded-xl border px-3 py-2 text-xs",
                current.is_alive
                  ? "border-success/20 bg-success/10 text-success"
                  : "border-danger/20 bg-danger/10 text-danger",
              )}
            >
              {current.is_alive ? (
                <CheckCircle2 className="size-4 shrink-0" />
              ) : (
                <AlertTriangle className="size-4 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-medium">
                  {current.is_alive ? "Прокси активен" : "Прокси недоступен"}
                </div>
                {current.last_error && (
                  <div className="mt-0.5 truncate opacity-80">{current.last_error}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
